import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, Lock, ArrowRight, FileText, Target, Briefcase, Mic, User } from 'lucide-react';
import { API_BASE } from '../config';

interface OnboardingStats {
  resume: boolean;
  profile: boolean;
  targeting: boolean;
  documents: boolean;
  practiceTrack: boolean;
  sources: {
    trackerApplications: number;
    interviewSessions: number;
    analyses: number;
    evaluations: number;
    generatedApplications: number;
  };
}

const fallback: OnboardingStats = {
  resume: false,
  profile: false,
  targeting: false,
  documents: false,
  practiceTrack: false,
  sources: {
    trackerApplications: 0,
    interviewSessions: 0,
    analyses: 0,
    evaluations: 0,
    generatedApplications: 0,
  },
};

const StartHere: React.FC = () => {
  const [stats, setStats] = useState<OnboardingStats>(fallback);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE}/api/stats/onboarding`, { credentials: 'include' })
      .then((res) => res.ok ? res.json() : fallback)
      .then((data) => setStats({ ...fallback, ...data, sources: { ...fallback.sources, ...(data.sources ?? {}) } }))
      .catch(() => setStats(fallback))
      .finally(() => setLoading(false));
  }, []);

  const baseSteps = [
    { title: 'Career Profile', body: 'Set the role targets and skills ManSearch should optimize around.', done: stats.profile, href: '/settings', icon: User },
    { title: 'Resume Evidence', body: 'Upload a resume so generated documents can use real proof points.', done: stats.resume, href: '/resumes', icon: FileText },
    { title: 'Role Evaluation', body: 'Evaluate a role before generating application documents.', done: stats.targeting, href: '/job-targeting', icon: Target },
    { title: 'Documents', body: 'Generate application materials from a saved evaluation report.', done: stats.documents, href: '/job-targeting', icon: Briefcase },
    { title: 'Practice & Track', body: 'Create a tracker application or complete interview practice.', done: stats.practiceTrack, href: '/tracker', icon: Mic },
  ];

  // Sequential gating: a step is unlocked only once every step before it is done.
  let priorAllDone = true;
  const steps = baseSteps.map((step, i) => {
    const unlocked = priorAllDone;
    const lockedBy = unlocked ? null : baseSteps[i - 1].title;
    priorAllDone = priorAllDone && step.done;
    return { ...step, unlocked, lockedBy };
  });

  const complete = steps.filter((step) => step.done).length;
  const nextStep = steps.find((step) => step.unlocked && !step.done) ?? null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Start Here</h1>
        <p className="text-gray-600 dark:text-slate-400 mt-1">
          {loading ? 'Loading progress...' : `${complete} of ${steps.length} steps complete`}
        </p>
      </div>

      {/* Guided next-action CTA */}
      {!loading && (
        <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-5 flex items-center justify-between gap-4">
          {nextStep ? (
            <>
              <div>
                <p className="text-sm text-primary-700 dark:text-primary-300 font-medium">Next step</p>
                <p className="text-gray-900 dark:text-white font-semibold">{nextStep.title}</p>
                <p className="text-sm text-gray-600 dark:text-slate-400 mt-0.5">{nextStep.body}</p>
              </div>
              <Link
                to={nextStep.href}
                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white font-medium rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all whitespace-nowrap"
              >
                <span>Continue</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </>
          ) : (
            <p className="text-gray-900 dark:text-white font-semibold">🎉 You&apos;re all set — your onboarding is complete.</p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
        {steps.map((step) => {
          const cardClass =
            'bg-white dark:bg-navy-800 border rounded-lg p-4 transition-colors block ' +
            (step.unlocked
              ? 'border-gray-200 dark:border-slate-800 hover:border-primary-300 dark:hover:border-primary-700'
              : 'border-gray-200 dark:border-slate-800 opacity-60 cursor-not-allowed');

          const inner = (
            <>
              <div className="flex items-center justify-between mb-3">
                <step.icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                {step.done ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                ) : step.unlocked ? (
                  <Circle className="w-5 h-5 text-gray-300 dark:text-slate-600" />
                ) : (
                  <Lock className="w-5 h-5 text-gray-300 dark:text-slate-600" />
                )}
              </div>
              <h2 className="font-semibold text-gray-900 dark:text-white">{step.title}</h2>
              <p className="text-sm text-gray-600 dark:text-slate-400 mt-2">
                {step.unlocked ? step.body : `Complete "${step.lockedBy}" first.`}
              </p>
            </>
          );

          return step.unlocked ? (
            <Link key={step.title} to={step.href} className={cardClass}>
              {inner}
            </Link>
          ) : (
            <div key={step.title} className={cardClass} aria-disabled="true">
              {inner}
            </div>
          );
        })}
      </div>

      <div className="bg-white dark:bg-navy-800 border border-gray-200 dark:border-slate-800 rounded-lg p-5">
        <h2 className="font-semibold text-gray-900 dark:text-white mb-3">Progress Signals</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
          <Signal label="Tracked apps" value={stats.sources.trackerApplications} />
          <Signal label="Interview sessions" value={stats.sources.interviewSessions} />
          <Signal label="Analyses" value={stats.sources.analyses} />
          <Signal label="Evaluations" value={stats.sources.evaluations} />
          <Signal label="Generated docs" value={stats.sources.generatedApplications} />
        </div>
        <p className="text-xs text-gray-500 dark:text-slate-500 mt-4">
          Practice &amp; Track only completes from tracker or interview activity. Match analyses stay separate.
        </p>
      </div>
    </div>
  );
};

function Signal({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg bg-gray-50 dark:bg-slate-900 p-3">
      <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
      <p className="text-gray-600 dark:text-slate-400">{label}</p>
    </div>
  );
}

export default StartHere;
